import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { checkoutProgress } from '../checkout/checkout-progress.js';
import { OnrampApiError } from '../client.js';
import { fundingNavigationManifest } from '../generated/navigation.js';
import { useFundingFunnel } from '../operations/funnel-react.js';
import { fundingContactBack } from '../operations/navigation.js';
/** Owner-scoped contact status is memory-only; never persist or log contacts or codes. */
export function useContactVerification(client, forceConsent = false, purpose = 'guest') {
  const profileKind = purpose === 'profile_email' ? 'email' : purpose === 'profile_phone' ? 'phone' : null;
  const [profileVerified, setProfileVerified] = useState(null);
  const cache = useQueryClient();
  const progress = checkoutProgress(`${client.progressKey ?? client.cacheKey}:${purpose}`);
  const [contact, setContact] = useState(progress.contact);
  const [drafts, setDrafts] = useState(progress.drafts);
  const [acceptedHere, setAcceptedHere] = useState(false);
  const [challenge, updateChallenge] = useState(progress.challenge && progress.challenge.expiresAt > Date.now() ? progress.challenge : null);
  function setChallenge(value) {
    progress.challenge = value;
    updateChallenge(value);
  }
  function editContact(value_0) {
    progress.contact = value_0;
    setContact(value_0);
  }
  const [pendingRefresh, setPendingRefresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [clock, setClock] = useState(Date.now());
  const active = useRef(null);
  const queryKey = ['onramp', client.cacheKey, 'payer'];
  const status = useQuery({
    queryKey,
    queryFn: ({
      signal
    }) => client.getPayerStatus(signal),
    retry: false,
    gcTime: 0,
    staleTime: 0,
    meta: {
      persist: false
    }
  });
  useEffect(() => {
    const stop = () => {
      active.current?.abort();
      cache.removeQueries({
        queryKey: ['onramp', client.cacheKey, 'payer']
      });
    };
    const unsubscribe = client.onDispose(stop);
    return () => {
      unsubscribe();
      stop();
    };
  }, [client, cache]);
  const next = profileKind ? profileVerified === purpose ? 'ready' : profileKind : purpose === 'phone' ? status.data?.phoneVerified ? 'ready' : 'phone' : !status.data?.emailVerified ? 'email' : !status.data.phoneVerified ? 'phone' : status.data.consentRequired !== false && !status.data.consentAccepted || forceConsent && !acceptedHere ? 'consent' : 'ready';
  const kind = challenge?.kind ?? contact ?? (next === 'phone' ? 'phone' : 'email');
  const cooldown = (progress.cooldowns[kind] ?? 0) > clock;
  const resendInSeconds = Math.max(0, Math.ceil(((progress.cooldowns[kind] ?? 0) - clock) / 1000));
  useEffect(() => {
    if (!cooldown) return;
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);
  useFundingFunnel(client, {
    stage: challenge ? `${kind}_code` : contact ?? (next === 'ready' ? 'contact_ready' : next),
    outcome: status.isPending || busy ? 'loading' : error || status.isError ? 'error' : 'entered',
    errorCode: error ?? (status.isError ? 'verification_failed' : null)
  });
  async function run(action) {
    if (active.current) return;
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setError(null);
    try {
      await action(controller.signal);
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure instanceof OnrampApiError && failure.status === 401 ? 'sign_in' : failure instanceof OnrampApiError && failure.status === 429 ? 'verification_rate_limited' : failure instanceof OnrampApiError && failure.status === 503 ? 'verification_delivery_failed' : 'verification_failed');
    } finally {
      active.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  }
  async function refreshVerifiedContact(accepted, signal_0) {
    const payer = await client.getPayerStatus(signal_0);
    if (signal_0.aborted) return;
    cache.setQueryData(queryKey, payer);
    if (purpose === 'guest' && accepted.consentVersion && payer.emailVerified && payer.phoneVerified && payer.consent?.version === accepted.consentVersion && (!payer.consentAccepted || forceConsent && !acceptedHere)) {
      const confirmed = await client.acceptConsent(accepted.consentVersion, signal_0);
      if (signal_0.aborted) return;
      cache.setQueryData(queryKey, confirmed);
      setAcceptedHere(confirmed.consentAccepted);
    }
    if (profileKind) {
      const canonical = profileKind === 'email' ? payer.verifiedEmail : payer.verifiedPhone;
      const expected = profileKind === 'email' ? (progress.drafts.email ?? '').trim().toLowerCase() : (progress.drafts.phone ?? '').replace(/[\s()-]/g, '');
      if (!canonical || canonical.toLowerCase() !== expected || !(profileKind === 'email' ? payer.emailVerified : payer.phoneVerified)) throw new Error('verified_contact_refresh_required');
      setProfileVerified(purpose);
    }
    setPendingRefresh(null);
  }
  function changeEmail() {
    if (active.current || purpose !== 'guest') return;
    // Abandon the phone challenge; only a verified replacement may advance.
    editContact('email');
    setChallenge(null);
    setError(null);
    setAcceptedHere(false);
    const updated = {
      ...progress.drafts,
      email: status.data?.verifiedEmail ?? drafts.email ?? ''
    };
    progress.drafts = updated;
    setDrafts(updated);
  }
  const screens = fundingNavigationManifest.contact.screens;
  return {
    step: pendingRefresh ? screens.refresh : status.isError ? screens.unavailable : challenge ? screens.code : screens[contact ?? next],
    kind,
    loading: status.isPending,
    busy,
    cooldown,
    resendInSeconds,
    verifiedEmail: purpose === 'guest' && status.data?.emailVerified ? status.data.verifiedEmail : null,
    draft: drafts[kind] ?? '',
    setDraft(value_1) {
      const updated_0 = {
        ...progress.drafts,
        [kind]: value_1
      };
      progress.drafts = updated_0;
      setDrafts(updated_0);
    },
    back() {
      if (active.current) return true;
      setError(null);
      if (profileKind) {
        if (!challenge) return false;
        editContact(profileKind);
        setChallenge(null);
        return true;
      }
      if (purpose === 'phone' && !challenge) return false;
      const target = fundingContactBack(kind, challenge ? 'code' : contact ?? next);
      if (target === 'amount') return false;
      if (target === 'email') changeEmail();else {
        editContact(target);
        setChallenge(null);
      }
      return true;
    },
    error: error ?? (status.error ? 'verification_failed' : null),
    consent: status.data?.consent ?? null,
    send: value_2 => run(async signal_1 => {
      if (cooldown) return;
      const result = kind === 'email' ? await client.startEmailVerification(value_2.trim(), signal_1) : await client.startPhoneVerification(value_2.replace(/[\s()-]/g, ''), signal_1);
      if (signal_1.aborted) return;
      const updated_1 = {
        ...progress.drafts,
        [kind]: value_2.trim()
      };
      progress.drafts = updated_1;
      setDrafts(updated_1);
      setChallenge({
        id: result.verificationId,
        kind,
        expiresAt: Date.now() + 600000
      });
      progress.cooldowns[kind] = Date.now() + (kind === 'phone' ? 30000 : 60000);
      setClock(Date.now());
    }),
    check: (code, consentVersion) => run(async signal_2 => {
      if (!challenge) return;
      const result_0 = challenge.kind === 'email' ? await client.checkEmailVerification(challenge.id, code, signal_2) : await client.checkPhoneVerification(challenge.id, code, signal_2);
      if (signal_2.aborted) return;
      if (!result_0.verified) {
        setError('code_invalid');
        return;
      }
      // Once consumed, retry the canonical read, never the OTP challenge.
      const accepted_0 = {
        consentVersion
      };
      setPendingRefresh(accepted_0);
      setChallenge(null);
      editContact(null);
      setClock(Date.now());
      await refreshVerifiedContact(accepted_0, signal_2);
    }),
    accept: () => run(async signal_3 => {
      if (purpose !== 'guest') return;
      const consent = status.data?.consent;
      if (!consent) {
        setError('verification_failed');
        return;
      }
      const payer_0 = await client.acceptConsent(consent.version, signal_3);
      if (!signal_3.aborted) {
        cache.setQueryData(queryKey, payer_0);
        setAcceptedHere(payer_0.consentAccepted);
      }
    }),
    edit: () => {
      if (!active.current) {
        editContact(kind);
        setChallenge(null);
        setError(null);
      }
    },
    changeEmail,
    retry: () => {
      setError(null);
      if (pendingRefresh) void run(signal_4 => refreshVerifiedContact(pendingRefresh, signal_4));else void status.refetch();
    }
  };
}
/** Contact replacement is isolated from checkout drafts and never accepts payment terms. */
export function useFundingProfileContact(client, kind) {
  return useContactVerification(client, false, kind === "email" ? "profile_email" : "profile_phone");
}